import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { AppBar, Toolbar } from '@mui/material';

import { fetchOrganization } from 'store/actions/organizations';
import { fetchAuthStatus } from 'store/actions/auth';
import { organizationSelectors } from 'store/selectors/organizations';
import { authSelectors } from 'store/selectors/auth';

import { Backdrop, LinkText } from 'common/components';
import Container from 'common/components/Container';
import { nonAuthenticatedRoutes, nonOrganizationRoutes } from 'utils/constants';
import mediaQueries from 'utils/mediaQueries';
import Snackbar from './Snackbar';

import Analytics from './Analytics';
import ScrollToTop from './ScrollToTop';
import Navigation from './Navigation';
import UserDropdown from './UserDropdown';

const { small } = mediaQueries;

const LogoMark = styled.img`
  max-height: 40px;
  padding: 4px 10px 0 0;
`;

const LogoType = styled.img`
  max-height: 40px;
  padding: 9px 0 5px;
`;

// TEMP: waiting for new design for header
// const Logo = styled.img`
//   max-height: 50px;
//   margin-left: 58px;

//   @media ${small} {
//     max-height: 40px;
//     margin-left: 0;
//   }
// `;

const LogoLink = styled(LinkText)`
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};

  @media ${small} {
    flex: 1;
  }
`;

const StyledAppBar = styled(AppBar)`
  justify-content: center;
  align-items: center;
  height: 80px;
  box-shadow: none;
  background-color: ${({ theme }) => theme.colors.headerBackground};
`;

const StyledMain = styled.main`
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  background-color: ${({ $hasGreyBackground, theme }) =>
    $hasGreyBackground ? theme.colors.pageBackground : theme.colors.white};
`;

const StyledToolBar = styled(Toolbar)`
  flex-grow: 1;
`;

const { REACT_APP_INTERCOM_ID } = process.env;

/**
 * Application shell wrapped around every routed page. It checks auth status on
 * load (redirecting to /login or /organizations as needed), fetches the
 * current organization from the route's uuid into the store, boots the
 * Intercom widget, and renders the top app bar (logo, `Navigation`,
 * `UserDropdown`), `Snackbar`, `Analytics`, and the page content — with a grey
 * page background on a fixed set of routes.
 */
const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);

  const { isAuthenticated, organization } = useSelector(state => ({
    isAuthenticated: authSelectors.getIsAuthenticated(state),
    organization: organizationSelectors.getOrganization(state),
  }));

  const matchOrganizationUuid = useRouteMatch({
    path: '/:organizationUuid',
    strict: true,
  });
  const matchOrganizations = useRouteMatch({
    path: '/organizations',
    strict: true,
  });
  const matchActionCenterDetails = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/campaigns/:campaignUuid/action-centers/:actionCenterUuid',
    strict: true,
  });
  const matchActionAlertDetails = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/campaigns/:campaignUuid/action-centers/:actionCenterUuid/action-alerts/:actionAlertUuid',
    strict: true,
  });
  const matchCampaignsDetails = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/campaigns',
    strict: true,
  });
  const matchCampaignDetails = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/campaigns/:campaignUuid',
    strict: true,
  });
  const matchBroadcast = useRouteMatch({
    exact: false,
    path: '/:organizationUuid/broadcasts',
  });
  const matchSettingsDetails = useRouteMatch({
    path: '/:organizationUuid/settings',
    strict: true,
  });
  const matchTextBroadcastDetails = useRouteMatch({
    path: '/:organizationUuid/text-broadcasts/:textBroadcastUuid',
    strict: true,
  });
  const matchTextActionAlertDetails = useRouteMatch({
    path: '/:organizationUuid/campaigns/:campaignUuid/action-centers/:actionCenterUuid/text-action-alerts/:actionAlertUuid',
    strict: true,
  });
  const matchCustomTargets = useRouteMatch({
    path: '/:organizationUuid/custom-targets',
    strict: true,
  });
  const matchMeetings = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/meetings',
    strict: true,
  });
  const matchMeetingDetails = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/meetings/:meetingUuid',
    strict: true,
  });
  const isMeetingDetails =
    !!matchMeetingDetails?.path && matchMeetingDetails?.params?.meetingUuid !== 'new';
  const matchTargetGroups = useRouteMatch({
    path: '/:organizationUuid/target-groups',
    strict: true,
  });
  const matchTemplates = useRouteMatch({
    path: '/:organizationUuid/templates',
    strict: true,
  });
  const matchTemplateDetails = useRouteMatch({
    path: '/:organizationUuid/:templateUuid/templates',
    strict: true,
  });
  const matchContactsLists = useRouteMatch({
    path: '/:organizationUuid/contacts/lists',
    strict: true,
  });
  const matchSignups = useRouteMatch({
    path: '/:organizationUuid/signups',
    strict: true,
  });
  const matchCustomFields = useRouteMatch({
    path: '/:organizationUuid/custom-fields',
    strict: true,
  });
  const matchAccount = useRouteMatch({
    exact: true,
    path: '/account',
    strict: true,
  });
  const matchCreateFilteredList = useRouteMatch({
    exact: true,
    path: '/:organizationUuid/contacts/lists/create-filtered-list',
    strict: true,
  });

  let organizationUuid = null;

  if (
    matchOrganizationUuid?.params?.organizationUuid &&
    !Object.values(nonOrganizationRoutes).some(nonOrganizationRoute =>
      pathname.includes(nonOrganizationRoute)
    )
  ) {
    organizationUuid = matchOrganizationUuid?.params?.organizationUuid;
  }

  useEffect(() => {
    const fetchData = async () => {
      const authStatus = await dispatch(fetchAuthStatus());

      if (!authStatus.authenticated && !Object.values(nonAuthenticatedRoutes).includes(pathname)) {
        window.location = '/login';

        return;
      }

      if (authStatus.authenticated && window.location.pathname === '/login') {
        window.location = '/organizations';

        return;
      }

      if (Object.keys(organization) && organizationUuid) {
        await dispatch(fetchOrganization(organizationUuid)).catch(status => {
          if (status === 404) {
            window.location = '/organizations';
          }
        });
      }

      setIsLoaded(true);
    };

    fetchData();
  }, [dispatch, organizationUuid]);

  useEffect(() => {
    /* eslint-disable func-names */
    const w = window;
    const ic = w.Intercom;

    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const d = document;

      const i = function (...args) {
        i.c(args);
      };

      i.q = [];
      i.c = function (args) {
        i.q.push(args);
      };
      w.Intercom = i;
      const l = function () {
        const s = d.createElement('script');

        s.type = 'text/javascript';
        s.async = true;
        s.src = `https://widget.intercom.io/widget/${REACT_APP_INTERCOM_ID}/`;
        const x = d.getElementsByTagName('script')[0];

        x.parentNode.insertBefore(s, x);
      };

      if (document.readyState === 'complete') {
        l();
      } else if (w.attachEvent) {
        w.attachEvent('onload', l);
      } else {
        w.addEventListener('load', l, false);
      }
    }
  }, []);

  const handleHasGreyBackground = useMemo(() => {
    if (
      matchActionCenterDetails?.path ||
      matchCampaignsDetails?.path ||
      matchCampaignDetails?.path ||
      matchBroadcast?.path ||
      matchSettingsDetails?.path ||
      matchActionAlertDetails?.path ||
      matchTextBroadcastDetails?.path ||
      matchTextActionAlertDetails?.path ||
      matchCustomTargets?.path ||
      matchMeetings?.path ||
      isMeetingDetails ||
      matchTargetGroups?.path ||
      matchTemplates?.path ||
      matchTemplateDetails?.path ||
      (matchContactsLists?.path && !matchCreateFilteredList?.path) ||
      matchSignups?.path ||
      matchCustomFields?.path ||
      matchAccount?.path
    ) {
      return true;
    }

    return false;
  }, [
    matchActionCenterDetails?.path,
    matchCampaignsDetails?.path,
    matchCampaignDetails?.path,
    matchBroadcast?.path,
    matchSettingsDetails?.path,
    matchActionAlertDetails?.path,
    matchTextBroadcastDetails?.path,
    matchTextActionAlertDetails?.path,
    matchCustomTargets?.path,
    matchMeetings?.path,
    isMeetingDetails,
    matchTargetGroups?.path,
    matchTemplates?.path,
    matchTemplateDetails?.path,
    matchContactsLists?.path,
    matchCreateFilteredList?.path,
    matchSignups?.path,
    matchCustomFields?.path,
    matchAccount?.path,
  ]);

  return (
    <>
      <Analytics organizationUuid={organizationUuid} />
      {isAuthenticated && (
        <StyledAppBar position="relative">
          <ScrollToTop />
          <Container>
            <StyledToolBar disableGutters>
              <LogoLink disabled={matchOrganizations?.path} to="/organizations">
                <LogoMark alt="Muster logomark" src="/muster_logomark.png" />
                <LogoType alt="Muster logotype" src="/muster_logotype.png" />
              </LogoLink>
              <Navigation />
              <UserDropdown />
            </StyledToolBar>
          </Container>
          <Snackbar />
        </StyledAppBar>
      )}
      {isLoaded && <StyledMain $hasGreyBackground={handleHasGreyBackground}>{children}</StyledMain>}
      <Backdrop isOpen={!isLoaded} />
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
